/**
 * Two-step sign-in on the web (MFA_PLAN.md). The API says, on every sign-in
 * response and /auth/me, whether this account must verify (mfaRequired) and
 * whether this session already has (mfaVerified). The wall is the route.
 */
// What a level of account is offered. OFFERED prompts them to set it up (they
// may say not now); AVAILABLE is in their settings and never prompts.
export const MFA_OFFER = Object.freeze({NOT_OFFERED: 0, OFFERED: 1, ENFORCED: 2, AVAILABLE: 3});
export const MFA_OFFER_LABELS = Object.freeze({0: 'Not offered', 1: 'Offered', 2: 'Enforced', 3: 'Available'});
export const MFA_OFFER_SNOOZE_DAYS = 30;
const OFFER_SNOOZE_KEY = 'tl.mfaOfferSnooze.';

/**
 * Whether to prompt this account to set up two-step sign-in: only when its
 * level is OFFERED, nothing is enrolled, and they have not said "not now"
 * within the snooze. Never for Available, Enforced (that is the wall) or Not offered.
 */
export function mfaOfferPromptDue(user, snoozedUntilMs, nowMs = Date.now()) {
  if (!user?.id || user.mfaOffer !== MFA_OFFER.OFFERED || user.mfaEnrolled) return false;
  return !(snoozedUntilMs && snoozedUntilMs > nowMs);
}

export function readOfferSnooze(userId) {
  try {
    return Number(window.localStorage.getItem(OFFER_SNOOZE_KEY + userId)) || 0;
  } catch {
    return 0;
  }
}

export function snoozeOffer(userId, nowMs = Date.now()) {
  try {
    window.localStorage.setItem(OFFER_SNOOZE_KEY + userId, String(nowMs + MFA_OFFER_SNOOZE_DAYS * 86400000));
  } catch {
    // private window: they are asked again next visit
  }
}
export const MFA_SETUP_PATH = '/security/setup';
export const MFA_VERIFY_PATH = '/security/verify';
export const MFA_DEVICE_HEADER = 'X-Tl-Device';
const DEVICE_KEY = 'tl.mfaDevice';

/**
 * True while the two-step wall is owed. EVERY other wall (account set-up, a
 * required form, the user tag step) stands down while this is true: two walls
 * that each redirect to their own step send the browser round for ever
 * (12 Oct 2026, "Page unresponsive" after signing in as a super admin).
 */
export function mfaHeld(user) {
  return !!user?.id && user.mfaRequired === true && user.mfaVerified === false;
}

/**
 * Where a signed-in account that must verify is sent: the verify step, or the
 * set-up step when nothing is enrolled, with the route to come back to. Null
 * when nothing is owed, on the steps themselves, and on the exits every wall
 * leaves open (legal, contact, log out).
 */
export function mfaWallDestination(fullPath, user, exits = []) {
  if (!mfaHeld(user)) return null;
  const path = String(fullPath || '').split('?')[0];
  if (path.startsWith('/security/') || path.startsWith('/login-with-token')) return null;
  if (exits.some((e) => path.startsWith(e))) return null;
  const step = user.mfaEnrolled ? MFA_VERIFY_PATH : MFA_SETUP_PATH;
  return step + '?redirect=' + encodeURIComponent(fullPath || '/');
}

/** The remembered-device token, per browser (a convenience; the API holds the truth). */
export function readDeviceToken() {
  try {
    return window.localStorage.getItem(DEVICE_KEY) || null;
  } catch {
    return null;
  }
}

export function storeDeviceToken(token) {
  try {
    if (token) window.localStorage.setItem(DEVICE_KEY, token);
    else window.localStorage.removeItem(DEVICE_KEY);
  } catch {
    // private window: the device just is not remembered here
  }
}

/** A code box accepts "123 456" and "123456"; six digits or nothing. */
export function normaliseCode(raw) {
  return String(raw || '').replace(/\D/g, '').slice(0, 6);
}

/** A readable name for this browser, offered when remembering it. */
export function guessDeviceName() {
  if (typeof navigator === 'undefined') return 'This browser';
  const ua = navigator.userAgent || '';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Computer';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
  return `${browser} on ${os}`;
}
